/**
 * =============================================================
 * AADHAV ACADEMY – STUDENT PERFORMANCE & TOURNAMENT GAMES API
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE CONFIG
============================================================= */
const BASE = "/admin/students";

const ENDPOINTS = {
  PERFORMANCE: `${BASE}/student_performance.php`,
};

/* =============================================================
   RESPONSE HANDLER
============================================================= */
const handleResponse = (res, fallbackMessage, isSingle = false) => {
  if (!res || (res.status !== "success" && res.status !== true)) {
    return {
      success: false,
      message: res?.message || fallbackMessage,
      data: isSingle ? null : [],
      meta: {},
      summary: {},
    };
  }
  return {
    success: true,
    message: res.message || "Success",
    data: isSingle
      ? (res.data || null)
      : (Array.isArray(res.data) ? res.data : res.data ? [res.data] : []),
    meta: res.meta || {},
    summary: res.summary || {},
  };
};

/* =============================================================
   ERROR HANDLER
============================================================= */
const handleError = (error, label) => {
  console.error(`❌ ${label} Error:`, error);
  return {
    success: false,
    message: error?.message || "Unexpected error occurred",
    data: [],
    meta: {},
  };
};

/* =============================================================
   GET STUDENT PERFORMANCE (LIST / HISTORY / SINGLE)
============================================================= */
export const getStudentPerformance = async (
  studentId = null,
  tournamentPerformanceId = null,
  options = {}
) => {
  try {
    const params = { ...(options.params || {}) };
    if (studentId) params.student_id = studentId;

    const endpoint = tournamentPerformanceId
      ? `${ENDPOINTS.PERFORMANCE}?id=${tournamentPerformanceId}`
      : ENDPOINTS.PERFORMANCE;

    const res = await apiCall(
      "GET",
      endpoint,
      null,
      "Fetch Performance History",
      { ...options, params }
    );

    return handleResponse(
      res,
      "Failed to fetch performance data",
      Boolean(tournamentPerformanceId)
    );
  } catch (error) {
    return handleError(error, "Fetch Performance History");
  }
};

/* =============================================================
   FETCH CHESS OPENINGS MASTER LIST FROM API
============================================================= */
export const getChessOpenings = async (search = "", options = {}) => {
  try {
    const params = { action: "openings" };
    if (search) params.search = search;

    const res = await apiCall(
      "GET",
      ENDPOINTS.PERFORMANCE,
      null,
      "Fetch Chess Openings",
      { ...options, params }
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to fetch chess openings",
        data: [],
      };
    }

    return {
      success: true,
      message: res.message || "Openings Fetched Successfully",
      data: Array.isArray(res.data) ? res.data : [],
    };
  } catch (error) {
    return handleError(error, "Fetch Chess Openings");
  }
};

/* =============================================================
   CREATE TOURNAMENT PERFORMANCE
============================================================= */
export const createTournamentPerformance = async (payload, options = {}) => {
  try {
    const res = await apiCall(
      "POST",
      ENDPOINTS.PERFORMANCE,
      payload,
      "Create Tournament Performance",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to create performance",
      };
    }

    return {
      success: true,
      message: res.message || "Performance Created Successfully",
      data: res.data || null,
      id: res.student_tournament_id || res.id || null,
    };
  } catch (error) {
    return handleError(error, "Create Tournament Performance");
  }
};

/* =============================================================
   UPDATE TOURNAMENT PERFORMANCE
============================================================= */
export const updateTournamentPerformance = async (tournamentPerformanceId, payload, options = {}) => {
  try {
    if (!tournamentPerformanceId) {
      return { success: false, message: "Tournament Performance ID is required" };
    }

    const res = await apiCall(
      "PUT",
      `${ENDPOINTS.PERFORMANCE}?id=${tournamentPerformanceId}`,
      payload,
      "Update Tournament Performance",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to update performance",
      };
    }

    return {
      success: true,
      message: res.message || "Performance Updated Successfully",
      data: res.data || null,
    };
  } catch (error) {
    return handleError(error, "Update Tournament Performance");
  }
};

/* =============================================================
   DELETE TOURNAMENT PERFORMANCE
============================================================= */
export const deleteTournamentPerformance = async (tournamentPerformanceId, options = {}) => {
  try {
    if (!tournamentPerformanceId) {
      return { success: false, message: "Tournament Performance ID is required" };
    }

    const res = await apiCall(
      "DELETE",
      `${ENDPOINTS.PERFORMANCE}?id=${tournamentPerformanceId}`,
      null,
      "Delete Tournament Performance",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to delete tournament performance",
      };
    }

    return {
      success: true,
      message: res.message || "Tournament Performance Deleted Successfully",
    };
  } catch (error) {
    return handleError(error, "Delete Tournament Performance");
  }
};

/* =============================================================
   DELETE INDIVIDUAL TOURNAMENT GAME ROUND
============================================================= */
export const deleteTournamentGame = async (gameId, options = {}) => {
  try {
    if (!gameId) {
      return { success: false, message: "Game ID is required" };
    }

    const res = await apiCall(
      "DELETE",
      `${ENDPOINTS.PERFORMANCE}?game_id=${gameId}`,
      null,
      "Delete Individual Game Round",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to delete game round",
      };
    }

    return {
      success: true,
      message: res.message || "Game Round Deleted Successfully",
    };
  } catch (error) {
    return handleError(error, "Delete Individual Game Round");
  }
};